import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import "./App.css";
import Loading from "./utils/Loading";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { customerLogout } from "./features/auth/authSlice";

// Lazy imports
const Login = lazy(() => import("./admin/pages/Login"));
const Dashboard = lazy(() => import("./admin/pages/Dashboard"));
const BannerManagement = lazy(() => import("./admin/pages/BannerManagement"));
const SliderInfo = lazy(() => import("./admin/pages/SliderInfo"));
const Departments = lazy(() => import("./admin/pages/Departments"));
const ProductSizes = lazy(() => import("./admin/pages/ProductSizes"));
const ProductTypes = lazy(() => import("./admin/pages/ProductTypes"));
const ProductServices = lazy(() => import("./admin/pages/ProductServices"));
const Contact = lazy(() => import("./admin/pages/Contact"));
const Users = lazy(() => import("./admin/pages/Users"));
const MerchantData = lazy(() => import("./admin/pages/MerchantData"));
const TechnicalSupport = lazy(() => import("./admin/pages/TechnicalSupport"));
const Account = lazy(() => import("./admin/pages/Account"));
const Branches = lazy(() => import("./admin/pages/Branches"));
const FinancialReports = lazy(() => import("./admin/pages/FinancialReports"));
const Reservations = lazy(() => import("./admin/pages/Reservations"));
const MerchantReviews = lazy(() => import("./admin/pages/MerchantReviews"));
const Addons = lazy(() => import("./admin/pages/Addons"));
const ServiceProvider = lazy(() => import("./admin/pages/ServiceProvider"));
const DiscountCodes = lazy(() => import("./admin/pages/DiscountCodes"));
const ProductImages = lazy(() => import("./admin/pages/ProductImages"));
const PlaceSettings = lazy(() => import("./admin/pages/PlaceSettings"));

const Menu = lazy(() => import("./Menu"));
const ProductInfo = lazy(() => import("./ProductInfo"));
const ContactUs = lazy(() => import("./ContactUs"));

const CheckoutLogin = lazy(() => import("./CheckoutLogin"));
const CheckoutVerify = lazy(() => import("./CheckoutVerify"));
const OrderCheckout = lazy(() => import("./OrderCheckout"));
const ServiceSelection = lazy(() => import("./ServiceSelection"));
const LocationSelection = lazy(() => import("./LocationSelection"));
const CartPage = lazy(() => import("./CartPage"));
const OrdersPage = lazy(() => import("./OrdersPage"));
const OrderDetailsPage = lazy(() => import("./OrderDetailsPage"));
const ProfilePage = lazy(() => import("./ProfilePage"));
const EditProfilePage = lazy(() => import("./EditProfilePage"));
const FavoritesPage = lazy(() => import("./FavoritesPage"));
const AddressesPage = lazy(() => import("./AddressesPage"));
const AddAddressPage = lazy(() => import("./AddAddressPage"));
const CompleteAddress = lazy(() => import("./CompleteAddress"));
const PaymentSuccess = lazy(() => import("./PaymentSuccess"));
const PaymentError   = lazy(() => import("./PaymentError"));
const AboutUs       = lazy(() => import("./AboutUs"));
const WorkingTime   = lazy(() => import("./WorkingTime"));
const RatingsPage   = lazy(() => import("./RatingsPage"));
const AddRating     = lazy(() => import("./AddRating"));
const NotificationsPage = lazy(() => import("./NotificationsPage"));

const UserProtectedRoute = lazy(() => import("./components/UserProtectedRoute"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const PublicRoute = lazy(() => import("./components/PublicRoute"));

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Forced one-time logout as requested by the user
    if (!localStorage.getItem("forced_logout_done")) {
      dispatch(customerLogout());
      localStorage.setItem("forced_logout_done", "true");
    }
  }, [dispatch]);
  return (
    <Router>
    <Suspense fallback={<Loading />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/location-selection/:restaurant_name" element={<LocationSelection />} />
          <Route path="/product-info/:restaurant_name/:productId" element={<ProductInfo />} />
          <Route path="/contact-us/:restaurant_name" element={<ContactUs />} />
          <Route path="/checkout-login/:restaurant_name" element={<CheckoutLogin />} />
          <Route path="/checkout-verify/:restaurant_name" element={<CheckoutVerify />} />
          <Route path="/order-checkout/:restaurant_name" element={<OrderCheckout />} />
          <Route path="/service-selection/:restaurant_name" element={<ServiceSelection />} />
          <Route path="/cart/:restaurant_name" element={<CartPage />} />
          <Route path="/orders/:restaurant_name" element={<OrdersPage />} />
          <Route path="/order-details/:order_id/:restaurant_name" element={<OrderDetailsPage />} />
          <Route path="/profile/:restaurant_name" element={<ProfilePage />} />
          <Route path="/success-payment/:restaurant_name" element={<PaymentSuccess />} />
          <Route path="/payment-error/:restaurant_name" element={<PaymentError />} />
          <Route path="/about-us/:restaurant_name" element={<AboutUs />} />
          <Route path="/working-time/:restaurant_name" element={<WorkingTime />} />
          <Route path="/ratings/:restaurant_name" element={<RatingsPage />} />
          <Route path="/add-rating/:restaurant_name" element={<AddRating />} />
          <Route path="/notifications/:restaurant_name" element={<NotificationsPage />} />
          <Route path="/:restaurant_name" element={<Menu />} />

          {/* User Protected Routes */}
          <Route element={<UserProtectedRoute />}>
              <Route path="/edit-profile/:restaurant_name" element={<EditProfilePage />} />
              <Route path="/favorites/:restaurant_name" element={<FavoritesPage />} />
              <Route path="/addresses/:restaurant_name" element={<AddressesPage />} />
              <Route path="/add-address/:restaurant_name" element={<AddAddressPage />} />
              <Route path="/complete-address/:restaurant_name" element={<CompleteAddress />} />
          </Route>

          {/* PublicRoute Wrapper */}
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
          </Route>

          {/* Protected Admin Routes */}
          <Route element={<ProtectedRoute />}>
          <Route path="/test" element={<h1 style={{padding:20}}>Test Works</h1>} />

            <Route path="/admin/account" element={<Account />} />
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/merchant-data" element={<MerchantData />} />
            <Route path="/admin/branches" element={<Branches />} />
            <Route path="/admin/financial-reports" element={<FinancialReports />} />
            <Route path="/admin/reservations" element={<Reservations />} />
            <Route path="/admin/merchant-reviews" element={<MerchantReviews />} />
            <Route path="/admin/banner-management" element={<BannerManagement />} />
            <Route path="/admin/slider-info" element={<SliderInfo />} />
            <Route path="/admin/categories" element={<Departments />} />
            <Route path="/admin/product-sizes" element={<ProductSizes />} />
            <Route path="/admin/addons" element={<Addons />} />
            <Route path="/admin/product-types" element={<ProductTypes />} />
            <Route path="/admin/service-provider" element={<ServiceProvider />} />
            <Route path="/admin/product-services" element={<ProductServices />} />
            <Route path="/admin/discount-codes" element={<DiscountCodes />} />
            <Route path="/admin/product-images" element={<ProductImages />} />
            <Route path="/admin/place-settings" element={<PlaceSettings />} />
            <Route path="/admin/technical-support" element={<TechnicalSupport />} />
            <Route path="/admin/users" element={<Users />} />
            <Route path="/admin/contact" element={<Contact />} />
          </Route>

        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
